import React, { useMemo, memo, useState, useRef, useCallback } from 'react';
import cn from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { TouchableDiv } from '@components/common/Touchable';
import SimpleReactSelect from '@formElements/SimpleReactSelect/indexRevamp';
import { ReactComponent as Searchimg } from '../../../assets/images/SearchIcon.svg';
import style from './AppSidebarV2.module.scss';
import AppSidebarGroups from './AppSidebarGroups';
import { changeMenu, pushMenuItemsByScreenId } from '@reducers/orders';
import SearchModal from './Modal/MenuSearchModal/index';
import getDeviceType from '@utility/deviceType';
import DriveThruInfoV2 from './DriveThruInfoV2/index';

const AppSidebarV2 = ({
  check,
  serverAppTheme,
  menusList,
  menuIndex,
  changeMenu,
  pushMenuItemsByScreenId,
}) => {
  const dropDownRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [openKeyboard, setOpenKeyboard] = useState(false);
  const [searchModalOpen, setSearchModalOpen] = useState(false);
  const [isSearch, setIsSearch] = useState(false);
  const [driveThruSelections, setDriveThruSelections] = useState({
    lane: null,
    vehicleType: null,
    vehicleColor: null,
  });
  // serverApp is always a tablet/kiosk — show on-screen keyboard (matches orderScrollMenus logic: isCallCenter = false)
  const browserOrTablet = false;

  const { isCounter, isDriveThru } = getDeviceType();

  const isLandScape = useMemo(() => window.innerWidth > 799, [
    window.innerWidth,
  ]);
  const defaultMenu = useMemo(() => menusList[menuIndex] || {}, [
    menusList,
    menuIndex,
  ]);
  const [defaultValue, setDefaultValue] = useState(defaultMenu.id);

  const toggleMenu = useCallback(
    key => {
      const index = menusList.findIndex(option => option.id === key);
      if (menuIndex !== index && index !== -1) {
        window.sessionStorage.removeItem('menuTypeId');
        window.sessionStorage.removeItem('menuName');
        changeMenu(index);
        const firstScreenId = menusList[index]?.screens?.[0]?.id;
        if (firstScreenId) {
          pushMenuItemsByScreenId(firstScreenId);
        }
      }
    },
    [menusList, menuIndex, changeMenu, pushMenuItemsByScreenId]
  );
  const scrollMenuStyle = useMemo(() => {
    // const driveThruInfo = getObjectValue(
    //   () => JSON.parse(window.sessionStorage.getItem('auth'))?.driveThruInfo,
    //   null
    // );

    return {
      tipTileBackground: { background: serverAppTheme?.tip_tile },
      notificationTimeFont: {
        '--placeholder-color': serverAppTheme?.notification_time_font,
      },
      selectStyle: { color: serverAppTheme?.primary_text },
      scrollableContainer: {
        // TODO: Check Dynamic Height code integration later
        // height: driveThruInfo ? `calc(${dynamicHeight} - 1px)` : dynamicHeight,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        '&::-webkit-scrollbar': {
          display: 'none',
          width: 0,
          height: 0,
        },
      },
      menuList: {
        paddingBottom: '5px',
        minHeight: 'fit-content',
        flex: 1,
      },
    };
  }, [serverAppTheme]);

  const tablePopUp = useMemo(() => ({
    position: 'relative',
    padding: '15px',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    pointerEvents: 'auto',
    backgroundColor: serverAppTheme?.popUp_bg,
    backgroundClip: 'padding-box',
    height: '100%',
    border: serverAppTheme?.popupBorder,
    boxShadow: serverAppTheme?.popUp_box_shadow,
  }), [serverAppTheme]);
  
  return (
    <div
      className="col-2 col-md-2 h-100 d-flex flex-column position-relative"
      style={{
        zIndex: 10,
        backgroundColor: serverAppTheme?.popUp_bg || '#ffffff',
      }}
    >
      <div className="row px-2">
        <span className="fs_16 fw-500 pb-3 color-black"
        >#{check?.number}</span>
      </div>
      <div className="row px-1">
        <TouchableDiv
          role="button"
          tabIndex="0"
          className={cn(style.searchdiv, 'pl-3')}
          onClick={() => {
            setSearchModalOpen(true);
            setOpenKeyboard(true);
          }}
          style={{ ...scrollMenuStyle.tipTileBackground, position: 'relative', zIndex: 10 }}
        >
          <Searchimg stroke={serverAppTheme?.notification_time_font} />
          <span
            style={{
              ...scrollMenuStyle.notificationTimeFont,
            }}
            className={cn(style.searchStyle)}
          >
            Search
          </span>
        </TouchableDiv>
      </div>
      {menusList.length !== 0 && defaultValue && (
        <div className="row px-2">
          {menusList.length > 1 && (
            <div className={cn('w-100 my-2')} style={{ height: '32px' }}>
              <SimpleReactSelect
                screenType="orders"
                ref={dropDownRef}
                onSelect={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onChange={data => {
                  toggleMenu(data.id);
                  setDefaultValue(data.id);
                }}
                options={[...menusList]}
                defaultValue={defaultValue}
                name="menuCategory"
                isLandScape={isLandScape}
                isFocused={isFocused}
                serverAppTheme={serverAppTheme}
              />
            </div>
          )}
        </div>
      )}
      <AppSidebarGroups />
      {isDriveThru && (
        <div className="row">
          <DriveThruInfoV2
            check={check}
            onDriveThruSelectionChange={setDriveThruSelections}
          />
        </div>
      )}
      {searchModalOpen && (
        <SearchModal
          isSearch={isSearch}
          isOpen={searchModalOpen}
          serverAppTheme={serverAppTheme}
          customClass2={cn('w-100 h-100')}
          customClass={style.modalBg}
          setIsSearch={setIsSearch}
          setSearchModalOpen={setSearchModalOpen}
          isTop
          browserOrTablet={browserOrTablet}
          setOpenKeyboard={setOpenKeyboard}
          openKeyboard={openKeyboard}
          checkList
          style={tablePopUp}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ orders, theme }) => ({
  check: orders.seats,
  serverAppTheme: theme.serverAppTheme,
  menusList: orders.menusList,
  menuIndex: orders.menuIndex,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ changeMenu, pushMenuItemsByScreenId }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(memo(AppSidebarV2));
